/**
 * Owner: Masa Bayu
 * File: CommunitySections.tsx
 * Date:Time: 2026-10-17 04:26
 * Description: Dedicated community sections — Explore (trending tags), Saved (bookmarks),
 *   Notifications. Wired to the real QXCircle client API.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getQxCircleBookmarks,
  getQxCircleNotifications,
  getQxCircleTrendingTags,
  markAllQxCircleNotificationsRead,
  type QxCircleFeedPost,
  type QxCircleNotification,
  type QxCircleTrendingTagRow,
} from '../client/qxcircle-api';
import { CommunityPostCard } from './CommunityPostCard';

export interface CommunitySectionProps {
  readonly onNavigate: (path: string) => void;
}

export function CommunityExplore({ onNavigate }: CommunitySectionProps) {
  const [tags, setTags] = useState<readonly QxCircleTrendingTagRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getQxCircleTrendingTags()
      .then((response) => {
        if (alive) setTags(response.items);
      })
      .catch(() => {
        if (alive) setTags([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Discover</p>
        <h2 className="ddx__title">Explore</h2>
        <p className="ddx__lede">Trending tags across the circle. Tap one to open its feed.</p>
      </header>
      {loading ? (
        <p className="ddx__status">Loading trends…</p>
      ) : tags.length === 0 ? (
        <div className="ddx__empty">
          <h3>No trending tags yet</h3>
          <p>Tags appear here once posts use them.</p>
        </div>
      ) : (
        <div className="cmc__tags">
          {tags.map((tag) => (
            <button
              type="button"
              className="cmc__tab"
              key={tag.tag}
              onClick={() => {
                onNavigate(`/circle/tag/${encodeURIComponent(tag.tag)}`);
              }}
            >
              #{tag.tag} · {String(tag.postCount)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunitySaved({ onNavigate }: CommunitySectionProps) {
  const [posts, setPosts] = useState<readonly QxCircleFeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getQxCircleBookmarks()
      .then((response) => {
        if (alive) setPosts(response.items);
      })
      .catch(() => {
        if (alive) setPosts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Library</p>
        <h2 className="ddx__title">Saved</h2>
        <p className="ddx__lede">Posts you bookmarked to revisit.</p>
      </header>
      {loading ? (
        <p className="ddx__status">Loading saved…</p>
      ) : posts.length === 0 ? (
        <div className="ddx__empty">
          <h3>Nothing saved yet</h3>
          <p>Tap Save on any post to keep it here.</p>
          <button
            type="button"
            className="ddx__cta ddx__cta--solid"
            onClick={() => {
              onNavigate('/circle');
            }}
          >
            Back to feed
          </button>
        </div>
      ) : (
        <div className="cmc__feed">
          {posts.map((post) => (
            <CommunityPostCard key={post.id} post={post} onNavigate={onNavigate} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityNotifications({ onNavigate }: CommunitySectionProps) {
  const [items, setItems] = useState<readonly QxCircleNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getQxCircleNotifications()
      .then((response) => {
        if (alive) {
          setItems(response.items);
          setUnread(response.unreadCount);
        }
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Activity</p>
        <h2 className="ddx__title">Alerts</h2>
        <p className="ddx__lede">Likes, comments, follows and mentions directed at you.</p>
      </header>
      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta"
          disabled={unread === 0}
          onClick={() => {
            void markAllQxCircleNotificationsRead().then(load);
          }}
        >
          Mark all read
        </button>
      </div>
      {loading ? (
        <p className="ddx__status">Loading alerts…</p>
      ) : items.length === 0 ? (
        <div className="ddx__empty">
          <h3>No alerts yet</h3>
          <p>Activity on your posts and follows shows up here.</p>
        </div>
      ) : (
        <div className="cmc__list">
          {items.map((item) => (
            <div className="cmc__row" key={item.id}>
              {!item.read ? <span className="cmc__dot" aria-label="Unread" /> : null}
              <span className="cmc__row-main">
                <span className="cmc__row-title">{item.title}</span>
                <span className="cmc__row-sub">
                  {item.kind} · {item.body}
                </span>
              </span>
              <button
                type="button"
                className="cmc__action"
                onClick={() => {
                  onNavigate(item.href);
                }}
              >
                Open
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
